import { useCallback, useEffect, useState } from 'react';
import { de } from '@/i18n/de';
import { useAppStore } from '@/lib/store';
import { wsClient } from '@/lib/wsClient';
import { haptics } from '@/lib/haptics';
import { sound, unlockAudio } from '@/lib/sound';
import { pushBackGuard, useBackGuard } from '@/lib/backGuard';
import { rememberName } from '@/lib/recentName';
import { ConfirmSheet } from '@/components/ConfirmSheet';
import { ConnectionBanner } from '@/components/ConnectionBanner';
import { RejoinRequestPrompt } from '@/components/RejoinRequestPrompt';
import { Toasts } from '@/components/Toasts';
import { HomeScreen } from '@/screens/HomeScreen';
import { RejoinScreen } from '@/screens/RejoinScreen';
import { NameEntryScreen } from '@/screens/NameEntryScreen';
import { LobbyScreen } from '@/screens/LobbyScreen';
import { CountdownScreen } from '@/screens/CountdownScreen';
import { RoleRevealScreen } from '@/screens/RoleRevealScreen';
import { DiscussionScreen } from '@/screens/DiscussionScreen';
import { VotingScreen } from '@/screens/VotingScreen';
import { TieAnnounceScreen } from '@/screens/TieAnnounceScreen';
import { RoundResultScreen } from '@/screens/RoundResultScreen';
import { GameOverScreen } from '@/screens/GameOverScreen';
import { LocalMode } from '@/screens/local/LocalMode';

type PendingAction = { type: 'create' } | { type: 'join'; roomId: string } | null;

export function App() {
  const view = useAppStore((s) => s.view);
  const rejoinStatus = useAppStore((s) => s.rejoin?.status ?? null);
  const [pending, setPending] = useState<PendingAction>(null);
  const [localMode, setLocalMode] = useState(false);
  const [rejoining, setRejoining] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);

  useBackGuard(
    view !== null,
    useCallback(() => setLeaveOpen(true), []),
  );

  // Ein bestätigter Wiedereinstieg bringt den Raum von selbst mit — der
  // Suchbildschirm hat dann nichts mehr zu suchen.
  useEffect(() => {
    if (rejoinStatus === 'APPROVED') setRejoining(false);
  }, [rejoinStatus]);

  // Der Beitritt ist durch, sobald der Raum da ist. Bliebe die Absicht stehen,
  // landete man beim Verlassen wieder auf der Namenseingabe statt am Start.
  useEffect(() => {
    if (view) setPending(null);
  }, [view]);

  useEffect(() => {
    const unlockOnce = () => {
      unlockAudio();
      document.removeEventListener('pointerdown', unlockOnce);
    };
    document.addEventListener('pointerdown', unlockOnce);

    // Warum ein Raum weg ist, gehört dem Spieler gesagt — ein wortloser Sprung
    // zurück auf die Startseite wirkt wie ein Fehler.
    const unsubscribeClosed = wsClient.onRoomClosed((reason) => {
      if (reason === 'LEFT') return;
      useAppStore.getState().pushNotice(de.roomClosed[reason]);
    });

    const unsubscribe = wsClient.onRoomEvent((event) => {
      switch (event.type) {
        case 'PHASE_CHANGED':
          haptics.phaseChange();
          sound.phaseChange();
          return;
        case 'VOTE_RECEIVED':
          haptics.voteCast();
          sound.voteCast();
          return;
        case 'PLAYER_ELIMINATED':
        case 'NO_ELIMINATION':
        case 'TIE':
          haptics.eliminated();
          sound.eliminated();
          return;
        case 'GAME_OVER':
          haptics.gameOver();
          sound.gameOver();
          return;
        default:
          return;
      }
    });

    return () => {
      document.removeEventListener('pointerdown', unlockOnce);
      unsubscribe();
      unsubscribeClosed();
    };
  }, []);

  // Der Ein-Gerät-Modus kommt ohne Server aus — deshalb bewusst ohne
  // ConnectionBanner, sonst warnt die App vor einer Verbindung, die sie
  // gar nicht braucht.
  if (localMode) {
    return <LocalMode onExit={() => setLocalMode(false)} />;
  }

  if (!view) {
    if (rejoining) {
      return (
        <>
          <ConnectionBanner />
          <RejoinScreen onBack={() => setRejoining(false)} />
          <Toasts />
        </>
      );
    }
    if (pending) {
      return (
        <>
          <ConnectionBanner />
          <NameEntryScreen
            mode={pending.type}
            onBack={() => setPending(null)}
            onSubmit={(name) => {
              // Für den Weg zurück, falls dieser Tab das Token nicht überlebt.
              rememberName(name);
              if (pending.type === 'create') {
                wsClient.send({ type: 'room:create', playerName: name });
              } else {
                wsClient.send({ type: 'room:join', roomId: pending.roomId, playerName: name });
              }
            }}
          />
          <Toasts />
        </>
      );
    }
    return (
      <>
        <ConnectionBanner />
        <HomeScreen
          onCreate={() => setPending({ type: 'create' })}
          onJoin={(roomId) => setPending({ type: 'join', roomId })}
          onLocal={() => setLocalMode(true)}
          onRejoin={() => setRejoining(true)}
        />
        <Toasts />
      </>
    );
  }

  return (
    <>
      <ConnectionBanner />
      <PhaseTransition phase={view.phase} />
      <RejoinRequestPrompt />
      <ConfirmSheet
        open={leaveOpen}
        title={de.common.leaveRoomConfirm}
        body={de.common.leaveRoomBody}
        confirmLabel={de.common.leaveRoomConfirmCta}
        destructive
        onConfirm={() => {
          setLeaveOpen(false);
          wsClient.send({ type: 'room:leave' });
        }}
        onCancel={() => {
          setLeaveOpen(false);
          // Der Zurück-Tap hat den reservierten Eintrag verbraucht — ohne
          // Nachschub verließe der nächste Tap die App ohne Rückfrage.
          pushBackGuard();
        }}
      />
      <Toasts />
    </>
  );
}

/**
 * Weicher Schnitt zwischen den Phasen (GDD §32 „subtile Animationen").
 *
 * Der `key` erzwingt bei jedem Phasenwechsel ein frisches Element, sodass die
 * CSS-Animation erneut startet. Bewusst keine `AnimatePresence`: die koppelt
 * den Screenwechsel an eine JS-Animation, und die läuft nicht weiter, solange
 * der Tab keine Frames rendert. Ein gesperrtes Handy — der Normalfall zwischen
 * zwei Phasen — würde dann auf dem alten Screen hängen bleiben.
 *
 * `prefers-reduced-motion` deckt `theme.css` global für CSS-Animationen ab.
 */
function PhaseTransition({ phase }: { phase: string }) {
  return (
    <div key={phase} className="animate-phase-in">
      <GameScreens phase={phase} />
    </div>
  );
}

function GameScreens({ phase }: { phase: string }) {
  switch (phase) {
    case 'LOBBY':
      return <LobbyScreen />;
    case 'STARTING':
      return <CountdownScreen />;
    case 'ROLE_REVEAL':
      return <RoleRevealScreen />;
    case 'DISCUSSION':
      return <DiscussionScreen />;
    case 'VOTING':
      return <VotingScreen />;
    case 'TIE_BREAKER':
      return <TieAnnounceScreen />;
    case 'ROUND_RESULT':
      return <RoundResultScreen />;
    case 'GAME_OVER':
      return <GameOverScreen />;
    default:
      return null;
  }
}
